import React, { useState, useEffect, useCallback } from 'react';
import debounce from 'lodash.debounce';
import { sortBy, uniqBy } from 'lodash';
import IUserBasicInfo from '@Shared.Angular/@types/core/contracts/queryModel/user/UserBasicInfo';
import { Guid } from '@Shared.Angular/@types/guid';
import { getService } from '@Shared.Angular/flowingly.hooks/useService';
import { KendoReactComboBox } from '@Shared.Angular/kendo.react/KendoReactComboBox';

type Props = {
  businessId?: string;
  permissionSecureCodes?: string[];
  selectedUserId?: Guid;
  disabled?: boolean;
  onUserSelect?: (userId: Guid | undefined) => void;
  popupClassName?: string;
  inputId?: string;
  containerClass?: string;
  exclude?: Guid | Guid[];
  include?: Guid | Guid[];
  excludeInvitedUsers?: boolean;
  showEmail?: boolean;
  noEmpty?: boolean;
  noSpinner?: boolean;
  onDataLoadError?: () => void;
  loadUser?: (
    userId: Guid,
    fieldName: string,
    options: { businessId?: string }
  ) => Promise<IUserBasicInfo>;
};

type UserDisplayItem = IUserBasicInfo & {
  _selectText?: string;
  _selectValue?: Guid;
};

export const SelectUser = (props: Props) => {
  const {
    businessId,
    permissionSecureCodes,
    selectedUserId,
    disabled,
    onUserSelect,
    inputId,
    containerClass,
    exclude,
    include,
    excludeInvitedUsers,
    showEmail,
    noEmpty,
    noSpinner = false,
    onDataLoadError,
    loadUser
  } = props;

  const [users, setUsers] = useState<UserDisplayItem[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserDisplayItem | null>(
    null
  );
  const [searchTerm, setSearchTerm] = useState('');
  const currentSearchTermRef = React.useRef('');
  const lastFetchedSearchTermRef = React.useRef('');

  const userApiService = getService('userApiService');
  const sessionService = getService('sessionService');

  const formatUserDisplayText = useCallback(
    (user: IUserBasicInfo): UserDisplayItem => {
      const displayUser = user as UserDisplayItem;
      const userId = user.id;
      const fullName = user.fullName;
      const email = user.email;

      displayUser._selectValue = userId;

      const name = fullName || '';
      if (showEmail) {
        displayUser._selectText = email ? `${name} (${email})` : name;
      } else {
        displayUser._selectText = name;
      }

      return displayUser;
    },
    [showEmail]
  );

  const fetchSpecificUsers = useCallback(
    async (idsToFetch: Guid | Guid[]): Promise<UserDisplayItem[]> => {
      if (!idsToFetch) return [];

      const idsArray = Array.isArray(idsToFetch) ? idsToFetch : [idsToFetch];
      const businessIdFilter = businessId || sessionService.getBusinessId();

      try {
        const requests = idsArray.map((idToFetch) => {
          if (loadUser) {
            return loadUser(idToFetch, 'id', { businessId: businessIdFilter });
          }
          return userApiService.getUserBasicInfoByFieldName(idToFetch);
        });

        const results = await Promise.all(requests);
        return results
          .filter((user) => user != null)
          .map(formatUserDisplayText);
      } catch (err) {
        return [];
      }
    },
    [businessId, loadUser, sessionService, formatUserDisplayText]
  );

  const fetchUsers = async (searchTerm: string) => {
    // Skip fetch if search term hasn't changed after initial load
    if (searchTerm === lastFetchedSearchTermRef.current && users.length > 0) {
      return;
    }

    try {
      const searchUsersResult = await userApiService.searchUsers({
        term: searchTerm,
        permissionSecureCodes,
        excludeInvitedUsers,
        noSpinner: noSpinner
      });

      if (searchTerm !== currentSearchTermRef.current) {
        return;
      }

      lastFetchedSearchTermRef.current = searchTerm;

      let allUsers = searchUsersResult.map(formatUserDisplayText);

      // Include specific users if provided
      if (include) {
        const specificUsers = await fetchSpecificUsers(include);
        allUsers = [...allUsers, ...specificUsers];
      }

      // Exclude specific users if provided
      if (exclude) {
        const excludeSet = (Array.isArray(exclude) ? exclude : [exclude]).map(
          (id) => id.toLowerCase()
        );
        allUsers = allUsers.filter(
          (user) => !excludeSet.includes(user._selectValue.toLowerCase())
        );
      }

      // Remove duplicates and sort
      allUsers = sortBy(uniqBy(allUsers, '_selectValue'), '_selectText');

      // Always include selected user at the top if it exists
      if (selectedUser) {
        allUsers = [
          selectedUser,
          ...allUsers.filter(
            (user) => user._selectValue !== selectedUser._selectValue
          )
        ];
      }

      setUsers(allUsers || []);
    } catch (err) {
      setUsers([]);
      onDataLoadError?.();
    }
  };

  const fetchSelectedUser = useCallback(async () => {
    if (!selectedUserId) {
      if (noEmpty && selectedUser) {
        // If noEmpty is true, keep the current selection
        return;
      }
      setSelectedUser(null);
      return;
    }
    try {
      const selectedUserResult =
        await userApiService.getUserBasicInfoByFieldName(selectedUserId);
      if (selectedUserResult) {
        const selectedUserDisplay = formatUserDisplayText(selectedUserResult);

        setSelectedUser(selectedUserDisplay);
      }
    } catch (err) {
      setSelectedUser(null);
    }
  }, [selectedUserId, noEmpty, formatUserDisplayText]);

  const debouncedFetchUsers = useCallback(
    debounce((term: string) => {
      currentSearchTermRef.current = term;
      fetchUsers(term);
    }, 500),
    [
      businessId,
      permissionSecureCodes,
      selectedUser,
      exclude,
      include,
      showEmail,
      excludeInvitedUsers
    ]
  );

  useEffect(() => {
    debouncedFetchUsers(searchTerm);
    return () => {
      debouncedFetchUsers.cancel();
    };
  }, [searchTerm, debouncedFetchUsers]);

  useEffect(() => {
    fetchSelectedUser();
  }, [fetchSelectedUser]);

  const handleSelectionChange = (event) => {
    const newSelectedUser = event.value;

    // Implement noEmpty behavior
    if (noEmpty && !newSelectedUser && selectedUser) {
      // Prevent clearing - restore previous selection
      setSelectedUser(selectedUser);
      return;
    }

    setSelectedUser(newSelectedUser);
    onUserSelect?.(newSelectedUser?.id);
  };

  const handleFilterChange = (event) => {
    setSearchTerm(event.filter.value);
  };

  return (
    <KendoReactComboBox
      className={(containerClass || '') + ' select-user-combobox'}
      inputId={inputId || ''}
      popupClassName={props.popupClassName}
      data={users}
      textField="_selectText"
      dataItemKey="_selectValue"
      placeholder="Select User"
      value={selectedUser}
      filterable={true}
      clearButton={!noEmpty}
      disabled={disabled}
      onFilterChange={handleFilterChange}
      onChange={handleSelectionChange}
    />
  );
};
